// @flow

import { Component } from '../jsti/core';
import EventNames from '../constants/EventNames';

export default class CustomCheckbox extends Component {
  childHandler: function;
  identifier: string;
  childBoxes: NodeList<HTMLInputElement>;
  activeChildrenAmount: number = 0;

  /**
   * Initialize the component
   *
   * @param pEl
   */
  bind(pEl: HTMLElement): void {
    super.bind(pEl);
    this.setUp();

    this.childHandler = this.onChildChange.bind(this);
    this.addEventListeners();
  }

  /**
   * Validation and setup of properties
   */
  setUp(): void {
    const identifier = this.el.id;
    if (!identifier) {
      throw new Error('A unique identifier is required on the indeterminate checkbox');
    }
    this.identifier = identifier;

    this.childBoxes = this.children();
    this.activeChildrenAmount = this.countActiveChildren();
  }

  /**
   * Executes when one of the child checkboxes update
   */
  onChildChange(e: Event): void {
    if (!(e.target instanceof HTMLInputElement)) return;

    if (this.isChecked(e.target)) {
      this.activeChildrenAmount++;
    } else {
      this.activeChildrenAmount--;
    }
  }

  /**
   * Executes when the `group` checkbox is updated
   */
  onParentChange(): void {
    if (!(this.el instanceof HTMLInputElement)) return;
    if (this.isNotChecked(this.el)) {
      this.uncheckAllChildren();
    }
  }

  /**
   * Check all the children
   */
  checkAllChildren(): void {
    this.childBoxes.forEach((child: HTMLInputElement) => this.check(child));
    this.activeChildrenAmount = this.childBoxes.length;
  }

  /**
   * Uncheck all the children
   */
  uncheckAllChildren(): void {
    this.childBoxes.forEach((child: HTMLInputElement) => this.uncheck(child));
    this.activeChildrenAmount = 0;
  }

  /**
   * Retrieve all the checkboxes which belong to this component
   * By the identifier
   */
  children(): NodeList<HTMLInputElement> {
    return (document.querySelectorAll(`[ref="${this.identifier}-child"]`): any);
  }

  /**
   * Check the checkbox
   */
  check(element: HTMLInputElement): void {
    element.checked = true;
  }

  /**
   * Uncheck the checkbox
   */
  uncheck(element: HTMLInputElement): void {
    element.checked = false;
  }

  /**
   * Check if the checkbox is checked
   */
  isChecked(element: HTMLInputElement): boolean {
    return element.checked;
  }

  /**
   * Count the amount of active children
   */
  countActiveChildren(): number {
    let count: number = 0;
    this.childBoxes.forEach((child: HTMLInputElement) => {
      if (child.checked) {
        count++;
      }
    });
    return count;
  }

  /**
   * Check if a checkbox is not checked
   */
  isNotChecked(element: HTMLInputElement): boolean {
    return !this.isChecked(element);
  }

  /**
   * Add all event listeners
   */
  addEventListeners(): void {
    this.addEventListener(EventNames.CHANGE, this.onParentChange.bind(this));
    this.childBoxes.forEach((child: HTMLElement) => child.addEventListener(EventNames.CHANGE, this.childHandler));
  }

  /**
   * Remove all event listeners
   */
  removeEventListeners(): void {
    this.childBoxes.forEach((child: HTMLElement) => child.removeEventListener(EventNames.CHANGE, this.childHandler));
  }

  /**
   * Executes when the component is destroyed
   */
  destroy(): void {
    this.removeEventListeners();
    super.destroy();
  }
}
